import { create } from 'zustand';

import { getMobileAppInfo, initializeMessageListener, useAppMessageStore } from '@lemon/app-messages';

import { updateProfile } from '../api';
import { LANGUAGE_KEY, webCore } from '../core';

export type UserProfile = never;
export type UserView = never;

export interface WebCoreState {
    isInitialized: boolean;
    isAuthenticated: boolean;
    isOnMobileApp: boolean;
    error: Error | null;
    profile: UserProfile | null;
    userName: string;
}

export interface WebCoreStore extends WebCoreState {
    initialize: () => void;
    logout: () => Promise<void>;
    setIsAuthenticated: (isAuth: boolean) => void;
    setProfile: (profile: UserProfile) => void;
    updateProfile: (user: UserView) => Promise<void>;
}

/**
 * Initial state configuration for the web core store
 */
const initialState: Pick<WebCoreStore, keyof WebCoreState> = {
    isInitialized: false,
    isAuthenticated: false,
    error: null,
    profile: null,
    userName: '',
};

/**
 * Zustand store for managing web core state and actions
 */
export const useWebCoreStore = create<WebCoreStore>()(set => ({
    ...initialState,

    /**
     * Initializes the web core application
     * - Sets up authentication state
     * - Configures language preferences
     * - Handles initialization errors
     */
    initialize: async () => {
        set({ isInitialized: false, error: null });
        await webCore.init();
        await webCore.setUseXLemonLanguage(true, LANGUAGE_KEY);
        const isAuthenticated = await webCore.isAuthenticated();

        const { isOnMobileApp } = getMobileAppInfo();
        if (isOnMobileApp) {
            initializeMessageListener();

            // Add handler for mobile app token sync response
            const appMessageStore = useAppMessageStore.getState();
            appMessageStore.addHandler('SuccessSyncCredential', message => {
                console.log('📱 Mobile token sync successful:', message);
                // TODO: Process token data from mobile app
            });
        }
        set({ isInitialized: true, isAuthenticated, isOnMobileApp });
    },

    /**
     * Handles user logout
     * - Clears authentication state
     * - Removes user profile data
     * - Redirects to login page
     */
    logout: async () => {
        await webCore.logout();
        set({ isAuthenticated: false, profile: null, userName: '' });
        window.location.href = '/';
    },

    /**
     * Updates authentication state
     * @param isAuthenticated - New authentication state
     */
    setIsAuthenticated: (isAuthenticated: boolean) => set({ isAuthenticated }),

    /**
     * Updates user profile information
     * @param profile - User profile data
     */
    setProfile: (profile: UserProfile) =>
        set({
            profile,
            userName: profile['$user']?.name || 'Unknown',
        }),

    /**
     * Updates username and related profile information
     * @param user - Updated user view data
     */
    updateProfile: async (user: Partial<UserProfile>) => {
        const updated = await updateProfile(user);
        // TODO: set updated profile
        // set(state => {
        //     const profile = { ...state.profile, $user: user };
        //     const userName = user['name'];
        //     return { ...state, profile, userName };
        // });
    },
}));
